/**
 * Le pont entre l'horloge de position et l'horloge audio.
 *
 * # Deux horloges, et pourquoi il en faut deux
 *
 * La position — le tick, donc la tête de lecture et tout ce qui se dessine —
 * se calcule sur `performance.now()` : monotone, sub-milliseconde, et surtout
 * elle ne se suspend jamais. `AudioContext.currentTime`, lui, se fige dès que
 * le contexte est suspendu : onglet en arrière-plan, et surtout contexte
 * jamais démarré faute de geste de l'utilisateur. En séance, deux machines
 * dont l'une n'a pas encore cliqué divergent alors sans jamais se rattraper.
 *
 * L'ordonnancement audio, à l'inverse, ne peut vivre que sur l'horloge du
 * contexte : c'est la seule qui soit exacte à l'échantillon. Il faut donc
 * convertir, et `getOutputTimestamp()` est le seul point de contact fiable
 * entre les deux — il rend un couple corrélé, mesuré ensemble.
 *
 * # Quatre pièges, tous vérifiés
 *
 * 1. `contextTime` n'est **pas** `ctx.currentTime` : c'est la trame en cours
 *    de *sortie*, donc en retard de la latence de sortie. Pour revenir à
 *    l'échelle sur laquelle on programme — celle du rendu — il faut **ajouter**
 *    `outputLatency`.
 * 2. Le couple **dérive**. Les deux quartz diffèrent de quelques ppm, ce qui
 *    fait quelques microsecondes par seconde : invisible sur une mesure,
 *    audible sur un morceau. D'où le rééchantillonnage périodique plutôt
 *    qu'une constante calculée une fois.
 * 3. Il rend des **zéros** tant que le contexte n'a rien rendu. C'est le cas
 *    avant le premier geste de l'utilisateur, et c'est justement le moment où
 *    la position doit continuer d'avancer. D'où `isRendering` : le repli donne
 *    une conversion plausible, mais rien ne doit être ordonnancé tant qu'il est
 *    faux — des notes empilées sur une horloge figée partiraient toutes
 *    ensemble au réveil.
 * 4. Le relevé lui-même est **bruité**, et c'est celui qui a coûté le plus
 *    cher. `contextTime` est quantifié — la trame sortie, donc un multiple du
 *    quantum de rendu, voire du tampon de sortie : quelques millisecondes sous
 *    Chrome, une vingtaine sous iOS — et `outputLatency` est lui aussi rendu
 *    grossièrement. Remplacer l'écart par chaque relevé déplaçait donc la
 *    conversion de plusieurs millisecondes deux fois par seconde. Le roll, qui
 *    ne vit que sur l'horloge de position, restait parfaitement régulier ;
 *    l'audio, lui, sautait — « de temps en temps une note accroche ». D'où
 *    l'asservissement de [MAX_STEP_SECONDS] : l'écart tenu *se rapproche* du
 *    mesuré, il ne s'y jette pas.
 */

export interface AudioTimebase {
  /**
   * Vrai quand le contexte rend réellement des trames, donc quand une note
   * programmée sonnera à l'heure demandée. Faux avant le premier geste de
   * l'utilisateur : les conversions restent utilisables, l'ordonnancement non.
   */
  readonly isRendering: boolean;

  /** D'une position (échelle `performance.now()`) vers l'horloge du contexte. */
  toAudioTime(position: number): number;

  /** De l'horloge du contexte vers une position. */
  toPositionTime(audioTime: number): number;

  /**
   * Ce qui sépare l'instant où l'on programme de l'instant où l'on entend.
   *
   * Les deux conversions ci-dessus travaillent sur l'horloge du **rendu** —
   * la seule sur laquelle programmer ait un sens. Mais entre la trame rendue
   * et le haut-parleur il reste le tampon de sortie, et il n'est pas le même
   * d'une machine à l'autre : une vingtaine de millisecondes sur la sortie
   * intégrée d'un Mac, trois sur une interface. Deux machines qui posent la
   * même note au même instant de rendu ne l'entendent donc pas ensemble.
   *
   * Cette grandeur est ce qu'il faut retrancher pour viser l'**oreille** au
   * lieu du rendu. Voir `LocalTransport.getTimeFromTick`, seul endroit qui
   * s'en sert : c'est là que vit la grille, et c'est donc là que se décide ce
   * qu'elle date.
   */
  readonly outputLatencySeconds: number;
}

/**
 * Le retard entre la programmation et le son, pour un contexte donné.
 *
 * Les deux termes, et non `outputLatency` seul : `baseLatency` est ce que le
 * graphe met à rendre jusqu'au sous-système audio, `outputLatency` ce que le
 * sous-système met à sortir. Ce qui sépare « je programme » de « j'entends »
 * est bien leur somme — c'est aussi ce que chaque client annonce à la séance
 * sous le nom de `outputMs`, et les deux ne doivent pas diverger.
 *
 * Défini ici parce que c'est le module qui possède les horloges ; il est lu
 * depuis le contexte de séance pour l'annonce.
 */
export function outputLatencySeconds(context: AudioContext): number {
  return context.baseLatency + (context.outputLatency ?? 0);
}

/**
 * Les deux échelles confondues.
 *
 * Le repli des tests et de tout ce qui tourne sans contexte audio : la
 * conversion est l'identité, et l'ordonnancement est autorisé — sans quoi
 * aucun test de `Scheduler` ne pourrait jouer une note.
 */
export const IDENTITY_TIMEBASE: AudioTimebase = {
  isRendering: true,
  toAudioTime: (position) => position,
  toPositionTime: (audioTime) => audioTime,
  // Pas de carte son, donc pas de tampon de sortie : programmer et entendre
  // sont le même instant, et les tests gardent l'arithmétique qu'ils décrivent.
  outputLatencySeconds: 0,
};

/**
 * Période de rééchantillonnage, en secondes.
 *
 * Assez court pour que la dérive entre les deux quartz reste sous la
 * microseconde entre deux relevés, assez long pour que l'appel — qui n'est pas
 * gratuit — ne pèse pas dans la boucle d'ordonnancement, appelée toutes les
 * 25 ms.
 */
const RESAMPLE_SECONDS = 0.5;

/**
 * Période de relevé tant que le contexte ne rend pas, en secondes.
 *
 * Le pas du ticker d'ordonnancement : on regarde à chaque tour si le contexte
 * est reparti, et pas plus souvent. Voir `refresh` pour ce que la période
 * longue coûtait dans ce cas-là.
 */
const IDLE_RESAMPLE_SECONDS = 0.025;

/**
 * Correction maximale de l'écart par relevé, en secondes.
 *
 * # Pourquoi asservir plutôt que recopier
 *
 * Ce qu'on veut suivre et ce qu'on veut ignorer ne sont pas du même ordre de
 * grandeur, et c'est ce qui rend le réglage facile :
 *
 *   - la **dérive** à suivre vaut quelques ppm entre deux quartz, soit quelques
 *     microsecondes par seconde ;
 *   - le **bruit** à ignorer vaut la quantification de `contextTime`, soit
 *     quelques millisecondes — mille fois plus.
 *
 * 200 µs par relevé, à deux relevés par seconde, donnent 400 µs/s de capacité
 * de correction : cent fois la dérive réelle, et cinquante fois moins que le
 * bruit. Une excursion de 10 ms n'atteint donc jamais la conversion, tandis
 * qu'une dérive vraie est rattrapée sans qu'on la voie.
 *
 * L'écart absolu, lui, garde l'erreur du premier relevé — quelques
 * millisecondes de retard commun à toutes les notes. Personne ne l'entend :
 * ce qui s'entend, c'est qu'une note tombe ailleurs que ses voisines.
 */
const MAX_STEP_SECONDS = 0.0002;

/**
 * Relevés pendant lesquels le tampon de sortie s'installe encore.
 *
 * À un relevé toutes les demi-secondes, une dizaine de secondes : le temps que
 * le contexte rende, que le périphérique se cale, et que le navigateur cesse
 * d'annoncer des valeurs de démarrage. Rien n'est retenu pendant ce temps, et
 * c'est leur médiane qui l'est ensuite. Voir `sampleOutputLatency`.
 */
const OUTPUT_SETTLING_SAMPLES = 20;

/**
 * Au-delà de cet écart, ce n'est plus du bruit : on s'aligne d'un coup.
 *
 * Un changement de périphérique de sortie, une reprise après suspension, un
 * tampon qui double : la conversion doit suivre tout de suite. Le seuil est
 * placé bien au-dessus de la pire quantification observée (une vingtaine de
 * millisecondes sous iOS) pour qu'aucun bruit ne s'y engouffre.
 */
const JUMP_SECONDS = 0.05;

export class OutputTimestampTimebase implements AudioTimebase {
  /** Écart `audio − position`, en secondes. `null` tant qu'on n'a rien mesuré. */
  private skew: number | null = null;

  /** Position du dernier relevé, pour savoir quand en refaire un. */
  private sampledAt = Number.NEGATIVE_INFINITY;

  /**
   * Tampon de sortie tenu, asservi comme l'écart. `null` avant le premier
   * relevé. Voir le getter.
   */
  private output: number | null = null;

  /** Relevés pris pendant que le tampon de sortie s'installe. Voir `sampleOutputLatency`. */
  private settlingSamples: number[] = [];

  private rendering = false;

  constructor(
    private readonly ctx: AudioContext,
    private readonly resampleSeconds = RESAMPLE_SECONDS,
    private readonly maxStepSeconds = MAX_STEP_SECONDS,
  ) {}

  get isRendering(): boolean {
    this.refresh();

    return this.rendering;
  }

  toAudioTime(position: number): number {
    return position + this.currentSkew();
  }

  toPositionTime(audioTime: number): number {
    return audioTime - this.currentSkew();
  }

  /**
   * Le tampon de sortie, **asservi** — jamais la valeur brute.
   *
   * `ctx.outputLatency` est une estimation grossière, et elle *saute* : relevée
   * sur une sortie intégrée de Mac, elle bascule entre deux valeurs distantes
   * de huit millisecondes, par blocs de quelques secondes. La grandeur
   * physique, elle, ne bouge pas.
   *
   * Or on la retranche de l'instant de chaque note (voir
   * `LocalTransport.getTimeFromTick`) : suivre le relevé brut déplacerait donc
   * l'ordonnancement de huit millisecondes à chaque bascule, c'est-à-dire
   * fabriquerait exactement la gigue que [MAX_STEP_SECONDS] a été posé pour
   * éliminer sur l'écart. C'est le même défaut, au même endroit du parcours,
   * découvert deux fois.
   *
   * L'erreur absolue qui subsiste — quelques millisecondes de retard commun à
   * toutes les notes — ne s'entend pas. Ce qui s'entend, c'est qu'une note
   * tombe ailleurs que ses voisines.
   *
   * Le repli sur la valeur brute vaut tant que la mesure s'installe : rien
   * n'est retenu pendant les premières secondes, et rendre zéro y ferait
   * sonner les premières notes un tampon trop tard.
   */
  get outputLatencySeconds(): number {
    this.refresh();

    return this.output ?? outputLatencySeconds(this.ctx);
  }

  private currentSkew(): number {
    this.refresh();

    return this.skew ?? this.fallbackSkew();
  }

  /**
   * Le repli, tant qu'aucun horodatage n'est disponible.
   *
   * `currentTime` est la trame en cours de *rendu*, donc déjà sur la bonne
   * échelle — il lui manque seulement la correction de dérive, qui est nulle
   * par définition puisqu'on la relit à chaque appel. Il vaut zéro sur un
   * contexte jamais démarré, ce qui est exact : rien n'a été rendu.
   */
  private fallbackSkew(): number {
    return this.ctx.currentTime - now();
  }

  private refresh(): void {
    const position = now();

    // Tant que rien n'est rendu, on ne se rationne pas.
    //
    // La période d'une demi-seconde est là pour la **dérive** entre deux
    // quartz, qui ne se mesure que sur un contexte qui tourne. Appliquée à un
    // contexte arrêté, elle retardait d'autant le moment où l'on s'aperçoit
    // qu'il est reparti : jusqu'à une demi-seconde pendant laquelle
    // `ScheduleTicker` ne diffuse aucune fenêtre, donc pendant laquelle rien
    // n'est confié au synthé. Sur un iPhone qui revient d'arrière-plan, c'est
    // le début du morceau, ou la moitié d'un décompte, qui part au silence.
    //
    // Le plancher reste celui du ticker : un relevé par tour au plus, et
    // `getOutputTimestamp()` n'est pas appelé plus souvent qu'avant sur un
    // contexte qui rend.
    const period = this.rendering ? this.resampleSeconds : IDLE_RESAMPLE_SECONDS;

    if (position - this.sampledAt < period) {
      return;
    }

    this.sampledAt = position;

    const running = this.ctx.state === "running";

    this.sampleOutputLatency(running);

    // Sans `getOutputTimestamp` — un navigateur ancien, un contexte de test —
    // on retombe sur `currentTime`, et on se fie à l'état du contexte pour
    // savoir si l'on peut ordonnancer. C'est ce que faisait tout le studio
    // avant ce chantier : moins précis, mais pas faux.
    if (typeof this.ctx.getOutputTimestamp !== "function") {
      this.rendering = running;
      this.skew = null;

      return;
    }

    const stamp = this.ctx.getOutputTimestamp();

    // Des zéros : le contexte n'a rien rendu. Suspendu : il ne rendra rien
    // avant d'être repris. Dans les deux cas la conversion reste utilisable —
    // le roll doit continuer de défiler — mais rien ne doit être programmé.
    if (!running || !stamp.contextTime || !stamp.performanceTime) {
      this.rendering = false;
      this.skew = null;

      return;
    }

    // `contextTime` est la trame qu'on **entend** ; on programme sur celle
    // qu'on **rend**. D'où l'ajout de la latence de sortie, qui sépare les deux.
    const renderTime = stamp.contextTime + (this.ctx.outputLatency ?? 0);
    const measured = renderTime - stamp.performanceTime / 1000;

    this.rendering = true;
    this.skew = this.skew === null ? measured : this.approach(this.skew, measured);
  }

  /**
   * Relève le tampon de sortie, et le retient sans se laisser mener.
   *
   * # Deux bruits opposés, deux filtres
   *
   * En régime établi, `outputLatency` *oscille* : relevé sur une sortie
   * intégrée de Mac, il bascule entre deux valeurs distantes de huit
   * millisecondes, par blocs de quelques secondes, sans que rien n'ait changé
   * physiquement. L'asservissement le règle — il parque la valeur au milieu, et
   * un retard commun constant ne s'entend pas.
   *
   * Au démarrage, le relevé n'est pas encore une mesure : le temps que le
   * contexte et le périphérique s'installent, il vaut n'importe quoi. Sur un
   * Mac, trop grand — une centaine de millisecondes pour vingt. Sur un iPad,
   * trop petit — six millisecondes pour dix-neuf, relevé le 2026-09-12 sur les
   * haut-parleurs. Et un asservissement seul adopte ce premier chiffre tel
   * quel, puis met une demi-minute à une demi-heure à s'en défaire, à 400 µs
   * par seconde ; trop grand, il gonfle le délai de **toute la salle**, le
   * service retenant le plus grand tampon annoncé ; trop petit, il fait tomber
   * les notes de ce poste derrière la grille.
   *
   * Une première version tenait le *minimum* pendant l'installation, sur la
   * foi du Mac. L'iPad l'a démentie : le minimum s'accrochait à son
   * transitoire. On ne retient donc **rien** tant que la mesure s'installe —
   * la valeur rendue est le relevé brut, et la grille suit ses sauts, ce qui
   * ne coûte rien dans les dix premières secondes d'un contexte — puis la
   * **médiane** des relevés devient la valeur tenue : un transitoire à un bout
   * ou à l'autre de la fenêtre ne la déplace pas. L'asservissement prend le
   * relais ensuite.
   *
   * Rien n'est relevé sur un contexte à l'arrêt : il rend alors un tampon qui
   * ne décrit aucun périphérique. Et un `outputLatency` nul n'est pas un
   * relevé non plus : WebKit le rend tant que le contexte ne joue pas, et le
   * compter ferait d'un tampon inconnu un tampon de deux millisecondes.
   */
  private sampleOutputLatency(running: boolean): void {
    if (!running || (this.ctx.outputLatency ?? 0) <= 0) {
      return;
    }

    const reported = outputLatencySeconds(this.ctx);

    if (this.output !== null) {
      this.output = this.approach(this.output, reported);

      return;
    }

    this.settlingSamples.push(reported);

    if (this.settlingSamples.length < OUTPUT_SETTLING_SAMPLES) {
      return;
    }

    this.output = median(this.settlingSamples);
    this.settlingSamples = [];
  }

  /**
   * Rapproche l'écart tenu du dernier relevé, sans jamais sauter.
   *
   * C'est ici que le bruit de mesure est arrêté. Voir [MAX_STEP_SECONDS] pour
   * le rapport d'échelle qui rend ce filtre à la fois sûr et suffisant.
   */
  private approach(held: number, measured: number): number {
    const error = measured - held;

    if (Math.abs(error) > JUMP_SECONDS) {
      return measured;
    }

    return held + Math.sign(error) * Math.min(Math.abs(error), this.maxStepSeconds);
  }
}

function now(): number {
  return performance.now() / 1000;
}

function median(values: readonly number[]): number {
  const sorted = [...values].sort((a, b) => a - b);

  return sorted[Math.floor(sorted.length / 2)];
}
